import { z } from 'zod';

import { parseCsvString } from '@/utils/csv-parser';
import {
  type ComplianceStatus,
  KnownComplianceStatus,
} from '@/warranty-v3/entities/compliance-status';
import type { ConditionViolation } from '@/warranty-v3/entities/condition-violation';
import type { GuaranteedValue } from '@/warranty-v3/entities/guaranteed-value';
import {
  KnownUnitOverviewColumn,
  type UnitOverviewRow,
} from '@/warranty-v3/entities/overview';
import {
  KnownWarrantyCoverageStatus,
  type WarrantyCoverageStatus,
} from '@/warranty-v3/entities/warranty-coverage-status';
import type { WarrantyPenalty } from '@/warranty-v3/entities/warranty-penalty';

import { GetDataQuery } from '../get-data';

import conditionViolationsBanksCsv from './data/condition-violations-banks.csv?raw';
import conditionViolationsPacksCsv from './data/condition-violations-packs.csv?raw';
import unitOverviewBanksCsv from './data/unit-overview-banks.csv?raw';
import unitOverviewPacksCsv from './data/unit-overview-packs.csv?raw';
import warrantyPenaltiesBanksCsv from './data/warranty-penalties-banks.csv?raw';
import warrantyPenaltiesPacksCsv from './data/warranty-penalties-packs.csv?raw';

// Enum Mappings for Human-Readable Labels
enum KnownWarrantyPenaltyDescription {
  TEMPERATURE_HIGH = 'High Temperature',
  DOD_HIGH_CHA = 'High Depth of Discharge (Charge)',
  SOC_HIGH = 'High State of Charge',
  SOC_RESTING_HIGH = 'High Resting State of Charge',
  UNKNOWN = 'Unknown Penalty Type',
}

enum KnownConditionViolationDescription {
  TEMPERATURE_SPIKE = 'Temperature Spike',
  HIGH_VOLTAGE = 'High Voltage',
  HIGH_AVG_SOC = 'High Average State of Charge',
  UNKNOWN = 'Unknown Violation Type',
}

enum KnownDataStatus {
  NO_DATA = 'No Data',
  HAS_DATA = 'Has Data',
}

/**
 * Maps warranty penalty description enum codes to human-readable labels
 */
function mapWarrantyPenaltyDescription(description: string): string {
  switch (description) {
    case 'TEMPERATURE_HIGH':
      return KnownWarrantyPenaltyDescription.TEMPERATURE_HIGH;
    case 'DOD_HIGH_CHA':
      return KnownWarrantyPenaltyDescription.DOD_HIGH_CHA;
    case 'SOC_HIGH':
      return KnownWarrantyPenaltyDescription.SOC_HIGH;
    case 'SOC_RESTING_HIGH':
      return KnownWarrantyPenaltyDescription.SOC_RESTING_HIGH;
    default:
      return KnownWarrantyPenaltyDescription.UNKNOWN;
  }
}

/**
 * Maps condition violation description enum codes to human-readable labels
 */
function mapConditionViolationDescription(description: string): string {
  switch (description) {
    case 'TEMPERATURE_SPIKE':
      return KnownConditionViolationDescription.TEMPERATURE_SPIKE;
    case 'HIGH_VOLTAGE':
      return KnownConditionViolationDescription.HIGH_VOLTAGE;
    case 'HIGH_AVG_SOC':
      return KnownConditionViolationDescription.HIGH_AVG_SOC;
    default:
      return KnownConditionViolationDescription.UNKNOWN;
  }
}

/**
 * Maps data status code to human-readable label
 */
function mapDataStatusFromCode(code: number): string {
  switch (code) {
    case 0:
      return KnownDataStatus.NO_DATA;
    case 1:
      return KnownDataStatus.HAS_DATA;
    default:
      return KnownDataStatus.NO_DATA;
  }
}

// CSV Data Schemas
const UnitOverviewCSVSchema = z.object({
  system_accure_id: z.coerce.string(),
  bank_accure_id: z.coerce.string(),
  pack_accure_id: z.coerce.string().optional(),
  ageInYears: z.coerce.number(),
  complianceStatus: z.coerce.number(),
  cyclesTotal: z.coerce.number(),
  dataStatus: z.coerce.number(),
  fullFrontendId: z.coerce.string(),
  groupFullId: z.coerce.string(),
  id: z.coerce.string(),
  nUnitsOperationIssues: z.coerce.number(),
  nUnitsPerformanceIssues: z.coerce.number(),
  sohAccure: z.coerce.number(),
  sohGuaranteed: z.coerce.number(),
  sohPenaltyMax: z.coerce.number(),
  throughputTotal: z.coerce.number(),
  topFullId: z.coerce.string(),
  unitFullId: z.coerce.string(),
  warrantyCoverage: z.coerce.number(),
});

const WarrantyPenaltyCSVSchema = z.object({
  system_accure_id: z.coerce.string(),
  bank_accure_id: z.coerce.string(),
  pack_accure_id: z.coerce.string().optional(),
  description: z.coerce.string(),
  duration: z.coerce.number(),
  extremeValue: z.coerce.number(),
  fullFrontendId: z.coerce.string(),
  groupFullId: z.coerce.string(),
  id: z.coerce.string(),
  latestDetect: z.coerce.date(),
  sohPenalty: z.coerce.number(),
  topFullId: z.coerce.string(),
  totalCount: z.coerce.number(),
  unit: z.coerce.string(),
  unitFullId: z.coerce.string(),
});

const ConditionViolationCSVSchema = z.object({
  system_accure_id: z.coerce.string(),
  bank_accure_id: z.coerce.string(),
  pack_accure_id: z.coerce.string().optional(),
  affectedUnits: z.coerce.number(),
  description: z.coerce.string(),
  duration: z.coerce.number(),
  extremeValue: z.coerce.number(),
  fullFrontendId: z.coerce.string(),
  groupFullId: z.coerce.string(),
  id: z.coerce.string(),
  latestDetect: z.coerce.date(),
  topFullId: z.coerce.string(),
  unit: z.coerce.string(),
  unitFullId: z.coerce.string(),
});

type UnitOverviewCSV = z.infer<typeof UnitOverviewCSVSchema>;
type WarrantyPenaltyCSV = z.infer<typeof WarrantyPenaltyCSVSchema>;
type ConditionViolationCSV = z.infer<typeof ConditionViolationCSVSchema>;

// CSV Data Loaders
export const loadUnitOverviewPacksData = parseCsvString(
  unitOverviewPacksCsv,
  UnitOverviewCSVSchema,
);

export const loadUnitOverviewBanksData = parseCsvString(
  unitOverviewBanksCsv,
  UnitOverviewCSVSchema,
);

export const loadWarrantyPenaltyPacksData = parseCsvString(
  warrantyPenaltiesPacksCsv,
  WarrantyPenaltyCSVSchema,
);

export const loadWarrantyPenaltyBanksData = parseCsvString(
  warrantyPenaltiesBanksCsv,
  WarrantyPenaltyCSVSchema,
);

export const loadConditionViolationPacksData = parseCsvString(
  conditionViolationsPacksCsv,
  ConditionViolationCSVSchema,
);

export const loadConditionViolationBanksData = parseCsvString(
  conditionViolationsBanksCsv,
  ConditionViolationCSVSchema,
);

// Status Mappers
function mapComplianceStatusFromCode(code: number): ComplianceStatus {
  switch (code) {
    case 0:
      return KnownComplianceStatus.COMPLIANT;
    case 1:
      return KnownComplianceStatus.NON_COMPLIANT;
    case 2:
      return KnownComplianceStatus.AT_RISK;
    default:
      return KnownComplianceStatus.NO_DATA;
  }
}

function mapWarrantyCoverageStatusFromCode(
  code: number,
): WarrantyCoverageStatus {
  switch (code) {
    case 0:
      return KnownWarrantyCoverageStatus.EXPIRED;
    case 1:
      return KnownWarrantyCoverageStatus.CASE_OPEN;
    case 2:
      return KnownWarrantyCoverageStatus.VALID;
    default:
      return KnownWarrantyCoverageStatus.NO_DATA;
  }
}

// Constants
const DATA_STATUS = {
  NO_DATA: 0,
  HAS_DATA: 1,
} as const;

// Date shifting configuration
const OLDEST_STATIC_DATE = new Date('2024-02-15T23:59:59.999Z'); // Oldest date across all CSV files

/**
 * Calculate the runtime date shift delta
 * This shifts all static dates to appear current relative to now
 */
function calculateDateShiftDelta(): number {
  const now = new Date();
  return now.getTime() - OLDEST_STATIC_DATE.getTime();
}

/**
 * Apply runtime date shifting to a static date
 */
function shiftDateToNow(staticDate: Date): Date {
  const delta = calculateDateShiftDelta();
  const shiftedDate = new Date(staticDate.getTime() + delta);

  return shiftedDate;
}

// Business Logic
function getSuggestedAction(
  warrantyCoverage: WarrantyCoverageStatus,
  complianceStatus: ComplianceStatus,
  dataStatus: string,
): string | undefined {
  // Check for insufficient data
  if (dataStatus === mapDataStatusFromCode(DATA_STATUS.NO_DATA)) {
    return 'Insufficient data';
  }

  const isNonCompliant =
    complianceStatus === KnownComplianceStatus.NON_COMPLIANT;
  const isAtRisk = complianceStatus === KnownComplianceStatus.AT_RISK;
  const isCaseOpen = warrantyCoverage === KnownWarrantyCoverageStatus.CASE_OPEN;
  const isExpired = warrantyCoverage === KnownWarrantyCoverageStatus.EXPIRED;

  if (isNonCompliant && isCaseOpen) {
    return 'Review compliance and contact provider';
  }

  if (isNonCompliant) {
    return 'Review warranty compliance';
  }

  if (isAtRisk) {
    return 'Monitor and adjust operation';
  }

  if (isCaseOpen) {
    return 'Initiate capacity test';
  }

  if (isExpired) {
    return 'Consider replacement';
  }

  return undefined;
}

// Column Configuration
function getUnitOverviewColumns() {
  return [
    { column: KnownUnitOverviewColumn.FRONTEND_ID, order: 0 },
    { column: KnownUnitOverviewColumn.COVERAGE_STATUS, order: 1 },
    { column: KnownUnitOverviewColumn.COMPLIANCE_STATUS, order: 2 },
    { column: KnownUnitOverviewColumn.SUGGESTED_ACTION, order: 3 },
    { column: KnownUnitOverviewColumn.AGE_IN_YEARS, order: 4 },
    { column: KnownUnitOverviewColumn.CYCLES_TOTAL, order: 5 },
    { column: KnownUnitOverviewColumn.THROUGHPUT_TOTAL, order: 6 },
    { column: KnownUnitOverviewColumn.SOH, order: 7 },
    { column: KnownUnitOverviewColumn.GUARANTEED_SOH, order: 8 },
  ];
}

// Validation Functions
function isValidNumber(value: unknown): value is number {
  return (
    typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value)
  );
}

function validateFilteredData(
  data: FilteredData,
  filters: { systemId: string; packId?: string; bankId?: string },
): void {
  if (data.unitOverview.length === 0) {
    const filterType = filters.packId
      ? 'pack'
      : filters.bankId
        ? 'bank'
        : 'system';
    const filterId = filters.packId || filters.bankId || filters.systemId;
    throw new Error(
      `No data found for ${filterType}: ${filterId} (system_accure_id: ${filters.systemId})`,
    );
  }
}

// Data Mappers
function mapUnitOverviewCSVToRow(csv: UnitOverviewCSV): UnitOverviewRow {
  const complianceStatus = mapComplianceStatusFromCode(csv.complianceStatus);
  const coverageStatus = mapWarrantyCoverageStatusFromCode(
    csv.warrantyCoverage,
  );

  // Convert SOH percentages from 0-100 to 0-1 range
  const sohAccureNormalized = isValidNumber(csv.sohAccure)
    ? csv.sohAccure / 100
    : csv.sohAccure;
  const sohGuaranteedNormalized = isValidNumber(csv.sohGuaranteed)
    ? csv.sohGuaranteed / 100
    : csv.sohGuaranteed;
  const sohPenaltyMaxNormalized = isValidNumber(csv.sohPenaltyMax)
    ? csv.sohPenaltyMax / 100
    : csv.sohPenaltyMax;

  // Safe numeric calculations with validation
  const guaranteedSOH: GuaranteedValue | undefined =
    isValidNumber(sohGuaranteedNormalized) &&
    isValidNumber(sohPenaltyMaxNormalized)
      ? {
          base: sohGuaranteedNormalized,
          penalty: sohPenaltyMaxNormalized,
          effective: sohGuaranteedNormalized - sohPenaltyMaxNormalized,
        }
      : undefined;

  const dataStatus = mapDataStatusFromCode(csv.dataStatus);

  const suggestedAction = getSuggestedAction(
    coverageStatus,
    complianceStatus,
    dataStatus,
  );

  return {
    id: csv.id,
    frontendId: csv.fullFrontendId,
    coverageStatus,
    complianceStatus,
    suggestedAction,
    ageInYears: csv.ageInYears,
    cyclesTotal: csv.cyclesTotal,
    throughputTotal: csv.throughputTotal,
    soh: sohAccureNormalized,
    guaranteedSOH,
  };
}

function mapWarrantyPenaltyCSVToEntity(
  csv: WarrantyPenaltyCSV,
): WarrantyPenalty {
  // Apply runtime date shifting to make static dates appear current
  const shiftedDate = shiftDateToNow(csv.latestDetect);

  // Map description to human-readable label
  const description = mapWarrantyPenaltyDescription(csv.description);

  // Add prediction for SOC_RESTING_HIGH (0.25% additional penalty)
  const prediction =
    csv.description === 'SOC_RESTING_HIGH'
      ? {
          appliesAt: new Date(shiftedDate.getTime() + 90 * 24 * 60 * 60 * 1000), // 90 days from now
          penaltyDelta: 0.0025, // 0.25%
        }
      : undefined;

  return {
    id: csv.id,
    description,
    current: {
      lastDetectedAt: shiftedDate,
      penalty: csv.sohPenalty,
      extremeValue: csv.extremeValue,
      extremeValueUnit: csv.unit,
      durationHours: csv.duration,
      count: csv.totalCount,
    },
    prediction,
  };
}

/**
 * Generate historical data points for a condition violation
 * Creates a series showing the violation occurrence over time
 */
function generateViolationHistoryPoints(
  latestDetect: Date,
  affectedUnits: number,
  durationHours: number,
): [Date, number | undefined][] {
  const points: [Date, number | undefined][] = [];

  // Generate points for the past 30 days
  const msPerDay = 24 * 60 * 60 * 1000;
  const msPerHour = 60 * 60 * 1000;

  // Start from 30 days before the latest detection
  const startDate = new Date(latestDetect.getTime() - 30 * msPerDay);

  for (let day = 0; day <= 30; day++) {
    const currentDate = new Date(startDate.getTime() + day * msPerDay);

    // Check if this day is within the violation period
    const violationStart = new Date(
      latestDetect.getTime() - durationHours * msPerHour,
    );

    if (currentDate >= violationStart && currentDate <= latestDetect) {
      // During violation - show the number of affected units
      points.push([currentDate, affectedUnits]);
    } else {
      // Outside violation - no value (undefined)
      points.push([currentDate, undefined]);
    }
  }

  return points;
}

function mapConditionViolationCSVToEntity(
  csv: ConditionViolationCSV,
): ConditionViolation {
  // Apply runtime date shifting to make static dates appear current
  const shiftedDate = shiftDateToNow(csv.latestDetect);

  // Map description to human-readable label
  const description = mapConditionViolationDescription(csv.description);

  // Generate historical points for the chart (shows affected units count)
  const historyPoints = generateViolationHistoryPoints(
    shiftedDate,
    csv.affectedUnits,
    csv.duration,
  );

  return {
    id: csv.id,
    description,
    extremeValue: csv.extremeValue,
    extremeValueUnit: csv.unit,
    lastDetectedAt: shiftedDate,
    durationHours: csv.duration,
    affectedUnitsCount: csv.affectedUnits,
    historySeries: { points: historyPoints },
  };
}

// Filtering Logic
function filterBySystem<T extends { system_accure_id: string }>(
  data: T[],
  systemId: string,
): T[] {
  return data.filter((item) => item.system_accure_id === systemId);
}

function filterByBank<T extends { bank_accure_id: string }>(
  data: T[],
  bankId: string,
): T[] {
  return data.filter((item) => item.bank_accure_id === bankId);
}

function filterByPack<T extends { pack_accure_id?: string }>(
  data: T[],
  packId: string,
): T[] {
  return data.filter((item) => item.pack_accure_id === packId);
}

// Utility Types
type FilteredData = {
  unitOverview: UnitOverviewCSV[];
  penalties: WarrantyPenaltyCSV[];
  violations: ConditionViolationCSV[];
};

// Data Loading & Filtering Utility
async function loadAndFilterData(
  systemId: string,
  packId?: string,
  bankId?: string,
): Promise<FilteredData> {
  try {
    if (packId) {
      // Load pack-level data and filter to specific pack
      const [unitOverview, penalties, violations] = await Promise.all([
        loadUnitOverviewPacksData,
        loadWarrantyPenaltyPacksData,
        loadConditionViolationPacksData,
      ]);

      return {
        unitOverview: filterByPack(
          filterBySystem(unitOverview, systemId),
          packId,
        ),
        penalties: filterByPack(filterBySystem(penalties, systemId), packId),
        violations: filterByPack(filterBySystem(violations, systemId), packId),
      };
    } else if (bankId) {
      // Load pack-level data and filter to specific bank
      const [unitOverview, penalties, violations] = await Promise.all([
        loadUnitOverviewPacksData,
        loadWarrantyPenaltyPacksData,
        loadConditionViolationPacksData,
      ]);

      return {
        unitOverview: filterByBank(
          filterBySystem(unitOverview, systemId),
          bankId,
        ),
        penalties: filterByBank(filterBySystem(penalties, systemId), bankId),
        violations: filterByBank(filterBySystem(violations, systemId), bankId),
      };
    } else {
      // Load bank-level data
      const [unitOverview, penalties, violations] = await Promise.all([
        loadUnitOverviewBanksData,
        loadWarrantyPenaltyBanksData,
        loadConditionViolationBanksData,
      ]);

      return {
        unitOverview: filterBySystem(unitOverview, systemId),
        penalties: filterBySystem(penalties, systemId),
        violations: filterBySystem(violations, systemId),
      };
    }
  } catch (error) {
    const filterContext = packId
      ? `pack: ${packId}`
      : bankId
        ? `bank: ${bankId}`
        : `system: ${systemId}`;
    throw new Error(
      `Failed to load data for ${filterContext}. ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

export async function dataResult(
  input: GetDataQuery.Input,
): Promise<GetDataQuery.Output> {
  const { systemId, selectedFilters } = input;
  const { bankId, packId } = selectedFilters;

  // Get column configuration once
  const columns = getUnitOverviewColumns();

  try {
    // Load and filter data based on filters
    const data = await loadAndFilterData(systemId, packId, bankId);

    // Validate that we got some data
    validateFilteredData(data, { systemId, packId, bankId });

    // Map CSV data to entities
    const unitOverviewRows = data.unitOverview.map(mapUnitOverviewCSVToRow);
    const warrantyPenalties = data.penalties.map(mapWarrantyPenaltyCSVToEntity);
    const conditionViolations = data.violations.map(
      mapConditionViolationCSVToEntity,
    );

    return {
      unitOverview: {
        columns,
        rows: unitOverviewRows,
      },
      warrantyPenalties,
      conditionViolations,
    };
  } catch (error) {
    // Log detailed error for debugging
    console.error('Failed to load warranty data:', {
      systemId,
      filters: selectedFilters,
      error: error instanceof Error ? error.message : error,
    });

    // Return empty data structure with proper structure
    return {
      unitOverview: {
        columns,
        rows: [],
      },
      warrantyPenalties: [],
      conditionViolations: [],
    };
  }
}
